import { PriceGridEntity, PriceGridRowKind } from './PriceGridEntity';
import { DEFAULT_PRICE_LADDER_DEPTH, PriceGridLadderType } from './ladder';
import type { PriceGridBucket, PriceGridSourceItem } from './types';

type SecurityWithSizeBuckets = PriceGridSourceItem['security'] & {
  SizeBuckets?: string[];
};

const PRICE_LADDER_PARENT_STEP_INDEX = 0;
const PRICE_LADDER_PARENT_BUCKET_COUNT = 1;

export interface ExpandSecurityToPriceGridRowsOptions<TSource extends PriceGridSourceItem = PriceGridSourceItem> {
  ladderType?: PriceGridLadderType;
  getBuckets?: (sourceItem: TSource) => PriceGridBucket[];
  getTreeKey?: (sourceItem: TSource) => string;
  getRowIDPrefix?: (sourceItem: TSource) => string;
}

function getVolumeBuckets<TSource extends PriceGridSourceItem>(sourceItem: TSource): PriceGridBucket[] {
  const security = sourceItem.security as SecurityWithSizeBuckets;
  const sizeBuckets = security.SizeBuckets?.filter(Boolean) ?? [];
  const sizes = sizeBuckets.length > 0 ? sizeBuckets : [security.NormalSize].filter(Boolean);

  return sizes.map((size, index) => ({
    id: sizeBuckets.length > 0 ? `bucket-${index}` : 'normal-size',
    size,
  }));
}

function getPriceLadderBuckets<TSource extends PriceGridSourceItem>(sourceItem: TSource): PriceGridBucket[] {
  const volumeBuckets = getVolumeBuckets(sourceItem);
  const parentSize = volumeBuckets.at(0)?.size;
  if (!parentSize) {
    return [];
  }

  const childCount =
    volumeBuckets.length > PRICE_LADDER_PARENT_BUCKET_COUNT
      ? volumeBuckets.length - PRICE_LADDER_PARENT_BUCKET_COUNT
      : DEFAULT_PRICE_LADDER_DEPTH - PRICE_LADDER_PARENT_BUCKET_COUNT;

  const parent: PriceGridBucket = {
    id: `price-step-${PRICE_LADDER_PARENT_STEP_INDEX}`,
    size: parentSize,
    priceStepIndex: PRICE_LADDER_PARENT_STEP_INDEX,
  };

  const children = Array.from({ length: childCount }, (_, index) => {
    const priceStepIndex = PRICE_LADDER_PARENT_BUCKET_COUNT + index;

    return {
      id: `price-step-${priceStepIndex}`,
      size: parentSize,
      priceStepIndex,
    };
  });

  return [parent, ...children];
}

function defaultGetBuckets<TSource extends PriceGridSourceItem>(
  sourceItem: TSource,
  ladderType: PriceGridLadderType
): PriceGridBucket[] {
  return ladderType === PriceGridLadderType.Book ? getPriceLadderBuckets(sourceItem) : getVolumeBuckets(sourceItem);
}

function defaultGetTreeKey<TSource extends PriceGridSourceItem>(sourceItem: TSource) {
  return sourceItem.security.Symbol;
}

function defaultGetRowIDPrefix<TSource extends PriceGridSourceItem>(sourceItem: TSource) {
  return sourceItem.id ?? sourceItem.security.Symbol;
}

export function expandSecurityToPriceGridRows<TSource extends PriceGridSourceItem = PriceGridSourceItem>(
  sourceItem: TSource,
  options: ExpandSecurityToPriceGridRowsOptions<TSource> = {}
): PriceGridEntity[] {
  const ladderType = options.ladderType ?? PriceGridLadderType.Ladder;
  const getBuckets = options.getBuckets ?? ((item: TSource) => defaultGetBuckets(item, ladderType));
  const getTreeKey = options.getTreeKey ?? defaultGetTreeKey;
  const getRowIDPrefix = options.getRowIDPrefix ?? defaultGetRowIDPrefix;

  const buckets = getBuckets(sourceItem);
  const parentBucket = buckets.at(0);
  if (!parentBucket) {
    return [];
  }

  const treeKey = getTreeKey(sourceItem);
  const rowIDPrefix = getRowIDPrefix(sourceItem);

  const parent = new PriceGridEntity({
    sourceItem,
    bucket: parentBucket,
    kind: PriceGridRowKind.Parent,
    rowID: `${rowIDPrefix}|${parentBucket.id}`,
    dataPath: [treeKey],
  });

  const children = buckets.slice(1).map(
    bucket =>
      new PriceGridEntity({
        sourceItem,
        bucket,
        kind: PriceGridRowKind.Child,
        rowID: `${rowIDPrefix}|${bucket.id}`,
        dataPath: [treeKey, `${treeKey}|${bucket.id}`],
      })
  );

  return [parent, ...children];
}

export function getPriceLadderDepth(sourceItem: PriceGridSourceItem): number {
  const volumeBuckets = getVolumeBuckets(sourceItem);
  return volumeBuckets.length > 1 ? volumeBuckets.length : DEFAULT_PRICE_LADDER_DEPTH;
}
